<template>
  <div>
    <v-toolbar dense :extended="filter" extension-height="80" elevation="1">
      <v-tooltip right color="blue">
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            color="blue"
            @click="addLineItem"
            :dark="selectedInvoice.client != null"
            :disabled="!selectedInvoice.client"
          >
            <font-awesome-icon icon="plus" size="lg" />
          </v-btn>
        </template>
        <span>Add Line Item</span>
      </v-tooltip>
      <v-tooltip right color="blue">
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            @click="filter = !filter"
            color="blue"
            :dark="hasBookings"
            class="ml-1"
            :disabled="!hasBookings"
          >
            <font-awesome-icon icon="filter" size="lg" />
          </v-btn>
        </template>
        <span>Filter</span>
      </v-tooltip>
      <v-spacer></v-spacer>
      <v-switch
        class="mr-5"
        v-model="selectedInvoice.groupCandidates"
        inset
        label="Group Candidates"
        hide-details
      ></v-switch>
      <v-switch
        class="mr-5"
        v-model="selectedInvoice.expand"
        inset
        label="Expand"
        hide-details
      ></v-switch>
      <v-switch
        class="mr-5"
        v-model="selectedInvoice.groupSites"
        inset
        label="Group Locations"
        hide-details
      ></v-switch>
      <v-spacer></v-spacer>
      <pdf :invoice="invoice" :disabled="selectedInvoice.client == null"></pdf>

      <template v-slot:extension v-if="filter">
        <v-container fluid>
          <v-row align="center" dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="filterSites"
                :items="bookingSites"
                label="Sites"
                placeholder="Select sites"
                filled
                outlined
                hide-details
                multiple
                small-chips
              >
                <!-- <template v-slot:selection="{ item, index }">
                  <v-chip close small @click:close="removeFilterSite( index)">
                    <span>{{ item }}</span>
                  </v-chip>
                </template>-->
              </v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="filterRoles"
                :items="bookingRoles"
                label="Roles"
                placeholder="Select roles"
                outlined
                filled
                hide-details
                multiple
                small-chips
              ></v-select>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </v-toolbar>
    <v-row>
      <v-col>
        <v-data-table
          :headers="headers"
          :items="displayItems"
          :items-per-page="200"
          hide-default-footer
          calculate-widths
          class="invoiceTable"
        >
          <template v-slot:item.site="{ item }">
            <div v-if="item.role != 'EXPENSE'">{{ item.site }}</div>
          </template>
          <template v-slot:item.wage="{ item }">
            <v-edit-dialog large cancel-text>
              {{ item.wage }}
              <template v-slot:input>
                <div class="pa-3">
                  <v-text-field
                    :value="item.wage.replace('£', '')"
                    @change="changeRate($event, item)"
                    label="Rate"
                    hide-details
                    type="number"
                  ></v-text-field>
                </div>
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:no-data>SELECT A CLIENT</template>

          <template v-slot:body.append="{}">
            <tr
              v-for="(item, index) in selectedInvoice.other"
              :key="'o' + index"
            >
              <td :colspan="2">
                <v-combobox
                  v-model="item.title"
                  :items="otherItems"
                  label="Title"
                  hide-details
                  dense
                  outlined
                  filled
                  prepend-icon="mdi-close-circle"
                  @click:prepend="selectedInvoice.other.splice(index, 1)"
                >
                </v-combobox>
              </td>
              <td :colspan="3">
                <v-text-field
                  v-model="item.description"
                  label="Description"
                  hide-details
                  dense
                  outlined
                  filled
                ></v-text-field>
              </td>
              <td :colspan="1">
                <div class="d-flex justify-center">
                  <v-text-field
                    v-model="item.quantity"
                    style="max-width: 70px"
                    label="Quantity"
                    hide-details
                    type="number"
                    dense
                    outlined
                    filled
                  ></v-text-field>
                </div>
              </td>
              <td :colspan="1">
                <div class="d-flex justify-center">
                  <v-currency-field
                    v-model="item.amount"
                    style="max-width: 90px"
                    label="Amount"
                    hide-details
                    dense
                    outlined
                    filled
                    currency="GBP"
                  />
                </div>
              </td>
              <td :colspan="1">
                <div style="max-width: 100px; text-align: right">
                  {{ toCurrency(item.amount * item.quantity) }}
                </div>
              </td>
            </tr>
          </template>

          <template v-slot:footer v-if="invoiceItems.length > 0">
            <v-divider class="pb-2"></v-divider>
            <div class="summaryDiv body-2">
              <div class="text-end summary">
                Subtotal:
                <span class="summaryValue">{{ toCurrency(subtotal) }}</span>
              </div>
              <div class="text-end summary">
                VAT Rate:
                <span class="summaryValue">{{ vatRate * 100 }}%</span>
              </div>
              <div class="text-end summary">
                Sales Tax:
                <span class="summaryValue">{{ toCurrency(salesTax) }}</span>
              </div>

              <div class="text-end summary font-weight-bold">
                Total:
                <span class="summaryValue">{{ toCurrency(total) }}</span>
              </div>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import moment, { min } from "moment";

import pdf from "@/components/pdf";

export default {
  props: {
    invoice: Object,
    id: String,
  },
  components: {
    pdf,
  },
  data() {
    return {
      items: [],
      selectedInvoice:
        !this.invoice || this.invoice === undefined
          ? { groupCandidates: false }
          : this.invoice,
      filter: true,
      filterRoles: [],
      filterSites: [],
      otherItems: ["Invoice Adjustment"],
      headers: [
        {
          text: "Candidate",
          value: "candidate",
          sortable: false,
        },
        {
          text: "Dates",
          value: "dates",
          sortable: false,
        },
        {
          text: "Role",
          value: "role",
          sortable: false,
        },
        {
          text: "Rate",
          value: "rateDesc",
          sortable: false,
        },
        {
          text: "Location",
          value: "site",
          sortable: false,
        },
        {
          text: "Extra Info",
          value: "info",
          sortable: false,
        },
        { text: "Hours", value: "hours", sortable: false, align: "center" },
        { text: "Wage/Hr", value: "wage", sortable: false, align: "center" },
        { text: "Amount", value: "amount", sortable: false, align: "end" },
      ],
    };
  },
  watch: {
    async selectedInvoice() {
      this.checkForData();
      this.filterRoles = this.getBookingRoles();
      this.filterSites = this.getBookingSites();
    },
    "selectedInvoice.date": function (val) {
      console.log(val);
      // this.selectedInvoice.start = moment(val)
      //   .utc()
      //   .format("YYYY-MM-DD");
      // this.selectedInvoice.finish = moment(val)
      //   .utc()
      //   .format("YYYY-MM-DD");
    },
    "invoice.client": function (val) {
      this.selectedInvoice = this.invoice;
      console.log("client changed");
      this.checkForData();
    },
  },
  computed: {
    ...mapState({
      invoices: (state) => state.invoices,
    }),

    hasBookings() {
      return (
        this.selectedInvoice.bookings &&
        this.selectedInvoice.bookings.length > 0
      );
    },

    bookingItems() {
      var items = [];

      if (!this.selectedInvoice.bookings) {
        return [];
      }

      var start = moment(this.selectedInvoice.start).startOf("day");
      var end = moment(this.selectedInvoice.finish).endOf("day");

      var bookings = this.selectedInvoice.bookings.filter((b) => {
        if (
          this.filterRoles.includes(b.role) &&
          this.filterSites.includes(b.site)
        ) {
          var bStart = moment(b.date);
          if (bStart.isSameOrAfter(start) && bStart.isSameOrBefore(end)) {
            return true;
          }
        }
      });

      var expenses = this.selectedInvoice.expenses.filter((b) => {
        var bStart = moment(b.start);
        if (bStart.isSameOrAfter(start) && bStart.isSameOrBefore(end)) {
          return true;
        }
      });

      this.selectedInvoice.filterExpenses = expenses;
      this.selectedInvoice.filterBookings = bookings;

      bookings.forEach((b) => {
        var wage = b.rate.rate;
        var overtime = b.rate.overtime;
        var hours = b.rate.hours;
        if (hours && b.rate.isBasic != false) {
          if (overtime && overtime > 0 && b.hours > hours) {
            items.push({
              ...b,
              rateDesc: b.rate.type,
              hours,
              wage,
            });

            var overtimeHours = b.hours - hours;
            items.push({
              ...b,
              rateDesc: b.rate.type + " Overtime",
              overtime: true,
              hours: overtimeHours,
              wage: overtime,
            });
          } else {
            items.push({
              ...b,
              rateDesc: b.rate.type,
              hours,
              wage,
            });
          }
        } else {
          items.push({
            ...b,
            rateDesc: b.rate.type,
            wage,
          });
        }
      });

      return items;
    },

    invoiceItems() {
      if (this.selectedInvoice.expand) {
        return this.bookingItems;
      } else {
        var helper = {};
        var result = this.bookingItems.reduce((r, o) => {
          var key;
          if (!this.selectedInvoice.groupCandidates) {
            key += o.candidate._id + "-";
          }
          key += o.role + "-" + o.rateDesc + "-" + o.wage;
          if (!this.selectedInvoice.groupSites) {
            key += "-" + o.site;
          }

          var date = moment(o.date).format("DD/MM");

          if (!helper[key]) {
            helper[key] = Object.assign({}, o); // create a copy of o
            helper[key].ids = [o._id];
            helper[key].sites = [o.site];
            helper[key].dates = [date];
            if (this.selectedInvoice.groupCandidates) {
              helper[key].candidate = [o.candidate._id];
            }
            if (o.info == null) {
              helper[key].info = [];
            } else {
              helper[key].info = [o.info];
            }
            r.push(helper[key]);
          } else {
            helper[key].ids == null
              ? (helper[key].ids = [o._id])
              : helper[key].ids.push(o._id);
            helper[key].hours += o.hours;
            helper[key].workHours += o.workHours;
            if (o.info != null && !helper[key].info.includes(o.info)) {
              helper[key].info.push(o.info);
            }
            if (!helper[key].sites.includes(o.site)) {
              helper[key].sites.push(o.site);
            }
            if (!helper[key].dates.includes(date)) {
              helper[key].dates.push(date);
            }
            if (this.selectedInvoice.groupCandidates) {
              if (!helper[key].candidate.includes(o.candidate._id)) {
                helper[key].candidate.push(o.candidate._id);
              }
            }
          }
          return r;
        }, []);
        console.log(result);
        return result;
      }
    },

    displayItems() {
      var invoiceItems = [];
      var expenseItems = [];
      var otherItems = [];

      console.log(this.invoiceItems);

      if (this.invoiceItems) {
        invoiceItems = this.invoiceItems.map((i) => {
          return {
            ...i,
            dates: this.selectedInvoice.expand
              ? moment(i.date).format("DD/MM")
              : i.dates.join(", "),
            candidate: i.candidate.name
              ? i.candidate.name
              : `${i.candidate.length} Worker${
                  i.candidate.length == 1 ? "" : "s"
                }`,
            site: !i.sites
              ? i.site
              : i.sites.length <= 2
              ? i.sites.join(", ")
              : `${i.sites.length} Sites`,

            info: this.selectedInvoice.expand ? i.info : i.info.join(", "),
            amount: this.toCurrency(i.hours * i.wage),
            wage: this.toCurrency(i.wage),
            bookingType: i.bookingType,
          };
        });
      }

      if (this.selectedInvoice.filterExpenses) {
        expenseItems = this.selectedInvoice.filterExpenses.map((e) => {
          return {
            candidate: e.candidate.name,
            site: e.type,
            rateDesc: e.type,
            hours: e.quantity,
            wage: this.toCurrency(e.amount),
            role: "EXPENSE",
            amount: this.toCurrency(e.quantity * e.amount),
            _id: e._id,
            bookingType: e.bookingType,
          };
        });
      }

      if (this.selectedInvoice.other) {
        otherItems = this.selectedInvoice.other.map((o) => {
          return {
            candidate: o.title,
            rateDesc: o.description,
            hours: o.quantity,
            wage: this.toCurrency(o.amount),
            amount: this.toCurrency(o.quantity * o.amount),
          };
        });
      }

      var items = [...invoiceItems, ...expenseItems, ...otherItems];
      this.$set(this.selectedInvoice, "items", items);
      return [...invoiceItems, ...expenseItems];
    },
    subtotal() {
      var subtotal = this.invoiceItems.reduce(function (r, o) {
        return r + o.wage * o.hours;
      }, 0);

      var expenses = this.selectedInvoice.expenses.reduce(function (r, o) {
        return r + o.quantity * o.amount;
      }, 0);

      var other = this.selectedInvoice.other.reduce(function (r, o) {
        return r + o.quantity * o.amount;
      }, 0);

      console.log(`Subtotal: ${subtotal}`);
      console.log(`Expenses: ${expenses}`);
      console.log(`Other: ${other}`);
      var total = parseFloat((subtotal + expenses + other).toFixed(2));
      console.log(`Total: ${total}`);
      this.$set(this.selectedInvoice, "subtotal", total);
      return total;
    },
    vatRate() {
      return 0.2;
    },
    salesTax() {
      var vat = (this.subtotal * this.vatRate).toFixed(2);
      vat = parseFloat(vat);
      this.$set(this.selectedInvoice, "vat", vat);
      return vat;
    },

    total() {
      return parseFloat(
        this.selectedInvoice.subtotal + this.selectedInvoice.vat
      ).toFixed(2);
    },
    bookingSites() {
      return this.getBookingSites();
    },
    bookingRoles() {
      return this.getBookingRoles();
    },
  },
  methods: {
    ...mapActions({
      fetchInvoiceById: "invoices/fetchById",
      fetchBookings: "bookings/fetchByInvoice",
    }),
    bookingDay(b) {
      var minutes = moment(b.finish).diff(moment(b.start), "minutes");
      var breaks = 0;
      if (b.breaks && b.breaks.includes(":")) {
        var a = b.breaks.split(":");
        breaks = (+a[0] * 60 * 60 + +a[1] * 60) / 60;
      }
      var hours = (minutes - breaks) / 60;

      return {
        _id: b._id,
        candidate: b.candidate,
        date: b.start,
        client: b.client,
        role: b.role,
        rate: b.rate,
        site: b.site,
        info: b.info,
        hours: hours,
        bookingType: b.bookingType,
      };
    },
    getBookingRoles() {
      if (!this.selectedInvoice.dbBookings) return [];
      var arr = this.selectedInvoice.dbBookings
        .filter((b) => b.role != null && b.role != undefined)
        .map((b) => {
          return b.role;
        });
      return [...new Set(arr)];
    },
    getBookingSites() {
      if (!this.selectedInvoice.dbBookings) return [];
      var arr = this.selectedInvoice.dbBookings
        .filter((b) => b.site != null && b.site != undefined)
        .map((b) => {
          return b.site;
        });
      return [...new Set(arr)];
    },
    toCurrency(val) {
      if (typeof val === "string") {
        val = parseFloat(val);
      }
      return "£" + val.toFixed(2);
    },
    checkForData() {
      if (
        !this.selectedInvoice.bookings ||
        this.selectedInvoice.bookings === undefined
      ) {
        this.$set(this.selectedInvoice, "bookings", []);
      }
      if (
        !this.selectedInvoice.expenses ||
        this.selectedInvoice.expenses === undefined
      ) {
        this.$set(this.selectedInvoice, "expenses", []);
      }
      if (
        !this.selectedInvoice.other ||
        this.selectedInvoice.other === undefined
      ) {
        this.$set(this.selectedInvoice, "other", []);
      }
      if (
        !this.selectedInvoice.date ||
        this.selectedInvoice.date === undefined
      ) {
        this.$set(this.selectedInvoice, "date", moment().toISOString());
      }
    },
    changeRate(rate, item) {
      // console.log(item);
      if (item.bookingType == 0) {
        var items = this.selectedInvoice.bookings.filter((i) => {
          return item.ids ? item.ids.includes(i._id) : item._id == i._id;
        });
        var rate = parseFloat(parseFloat(rate).toFixed(2));

        // console.log(items);
        items.forEach((i) => {
          if (item.overtime) {
            i.rate.overtime = rate;
          } else {
            i.rate.rate = rate;
          }
        });
      } else {
        var items = this.selectedInvoice.expenses.filter((i) => {
          return item._id == i._id;
        });
        var rate = parseFloat(parseFloat(rate).toFixed(2));

        items.forEach((i) => {
          i.amount = rate;
        });
      }
    },
    removeFilterRole(index) {
      this.filterRoles.splice(index, 1);
    },
    removeFilterSite(index) {
      this.filterSites.splice(index, 1);
    },

    addLineItem() {
      this.selectedInvoice.other.push({
        title: "",
        description: "",
        quantity: 0,
        amount: 0,
      });
    },
    async viewInvoice() {
      let item = this.invoices.invoiceDetails;

      var bookings = await this.fetchBookings(item._id);

      console.log("Bookings");
      console.log(bookings);

      this.isNew = false;

      var bookingItems = bookings.filter((b) => b.bookingType == 0);
      var expenseItems = bookings.filter((b) => b.bookingType == 1);
      var otherItems = item.other;

      var sorted = bookingItems.sort(function (a, b) {
        return Date.parse(a.start) > Date.parse(b.start);
      });

      var start = item.start;
      var finish = item.finish;
      if (sorted.length > 0) {
        if (start == null) {
          start = moment(sorted[0].start).startOf("isoWeek").toISOString();
        }
        if (finish == null) {
          finish = moment(sorted[sorted.length - 1].start)
            .endOf("isoWeek")
            .toISOString();
        }
      }

      this.selectedInvoice = {
        ...item,
        dbBookings: bookings,
        start,
        finish,
        bookings: bookingItems.map((b) => {
          return this.bookingDay(b);
        }),
        expenses: expenseItems,
        other: otherItems,
        subtotal: Math.abs(item.subtotal),
        vat: Math.abs(item.vat),
      };

      this.$set(this.selectedInvoice, "groupCandidates", false);

      this.originalItem = { ...this.selectedInvoice };

      if (bookings.length == 0) {
        if (item.subtotal > 0) {
          console.log("Blank");
          this.isBlank = true;
          this.isCredit = false;
          this.tab = 1;
        } else {
          this.isCredit = true;
          this.isBlank = false;
          this.tab = 2;
        }
        return;
      } else {
        this.isCredit = false;
        this.isBlank = false;
        this.tab = 0;
      }
    },
    async setGroupCandidate() {
      this.selectedInvoice.groupCandidates = false;
    },
  },
  async mounted() {
    this.setGroupCandidate();
    if (this.id) {
      await this.fetchInvoiceById({ invoice_id: this.id });
      await this.viewInvoice();
    }

    this.checkForData();
  },
};
</script>

<style>
.summaryDiv {
  float: right;
  padding: 10px;
}
.summary {
  border-bottom: 1px solid lightgray;
  padding: 5px;
  text-align: right;
}
.summaryValue {
  min-width: 80px;
  float: right;
}

.toolbar__extension {
  /* padding: 0px !important; */
}

.invoiceTable td {
  /* padding: 0 1px !important; */
  max-height: 40px !important;
  height: 40px !important;
}

.invoiceTable .theme--light.v-text-field--outlined fieldset {
  border-color: rgba(128, 128, 128, 0.2);
}

.invoiceTable
  .v-textarea.v-text-field--enclosed.v-text-field--outlined
  textarea {
  margin-top: 0px;
}

.invoiceTable
  .v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
  > .v-input__control
  > .v-input__slot {
  padding-left: 6px;
  padding-right: 2px;
}
</style>
